import { useEffect, useMemo, useState } from 'react'
import {
  GlobeAltIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { multilingualAPI } from '../services/api'

/**
 * Admin › Multilingual
 * -----------------------------------------------------------------------
 * Lists every customer with a per-row toggle. Flipping the toggle calls
 * PUT /admin/multilingual/customers/{id}/toggle. When ON, that customer's
 * chatbot widget shows a globe icon at the top and end-visitors can pick
 * a response language.
 */
export default function Multilingual() {
  const [customers, setCustomers] = useState([])
  const [supportedLanguages, setSupportedLanguages] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [togglingId, setTogglingId] = useState(null)

  const loadCustomers = async () => {
    try {
      setLoading(true)
      setError('')
      const res = await multilingualAPI.getCustomers({ per_page: 10000 })
      setCustomers(res.data.customers || [])
      setSupportedLanguages(res.data.supported_languages || {})
    } catch (err) {
      console.error('Failed to load multilingual customers:', err)
      setError('Failed to load customers. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCustomers()
  }, [])

  const handleToggle = async (customer) => {
    const nextValue = !customer.multilingual_enabled
    setTogglingId(customer.id)
    // Optimistic update
    setCustomers((prev) =>
      prev.map((c) =>
        c.id === customer.id ? { ...c, multilingual_enabled: nextValue } : c
      )
    )
    try {
      await multilingualAPI.toggle(customer.id, nextValue)
    } catch (err) {
      console.error('Toggle failed, reverting:', err)
      // Revert on failure
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === customer.id
            ? { ...c, multilingual_enabled: !nextValue }
            : c
        )
      )
      alert(
        `Failed to ${nextValue ? 'enable' : 'disable'} multilingual for ${
          customer.email
        }. Please try again.`
      )
    } finally {
      setTogglingId(null)
    }
  }

  const filteredCustomers = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return customers
    return customers.filter(
      (c) =>
        (c.email && c.email.toLowerCase().includes(q)) ||
        (c.full_name && c.full_name.toLowerCase().includes(q)) ||
        (c.company && c.company.toLowerCase().includes(q))
    )
  }, [customers, search])

  const enabledCount = useMemo(
    () => customers.filter((c) => c.multilingual_enabled).length,
    [customers]
  )
  const languageCount = Object.keys(supportedLanguages).length

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-50 rounded-lg">
            <GlobeAltIcon className="h-6 w-6 text-brand-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Multilingual Chatbot
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Enable per-customer multilingual support. When ON, the
              customer's widget shows a language picker (globe icon) at
              the top and visitors can choose their preferred response
              language.
            </p>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wide">
            Total customers
          </div>
          <div className="text-2xl font-semibold text-slate-900 mt-1">
            {customers.length}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wide">
            Multilingual enabled
          </div>
          <div className="text-2xl font-semibold text-emerald-600 mt-1">
            {enabledCount}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="text-xs text-slate-500 uppercase tracking-wide">
            Languages available
          </div>
          <div className="text-2xl font-semibold text-brand-600 mt-1">
            {languageCount}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email, name, or company…"
          className="w-full md:w-96 px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-slate-500">
            Loading customers…
          </div>
        ) : error ? (
          <div className="py-16 text-center text-red-600">{error}</div>
        ) : filteredCustomers.length === 0 ? (
          <div className="py-16 text-center text-slate-500">
            {search ? 'No customers match your search.' : 'No customers found.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Multilingual
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-slate-900">
                        {customer.full_name || '—'}
                      </div>
                      <div className="text-xs text-slate-500">
                        {customer.email}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-slate-700">
                        {customer.company || '—'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                        {customer.subscription_plan || 'free'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {customer.is_active && customer.is_verified ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                          <CheckCircleIcon className="h-4 w-4" />
                          Active
                        </span>
                      ) : !customer.is_verified ? (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                          <XCircleIcon className="h-4 w-4" />
                          Unverified
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          <XCircleIcon className="h-4 w-4" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ToggleSwitch
                        checked={customer.multilingual_enabled}
                        disabled={togglingId === customer.id}
                        onChange={() => handleToggle(customer)}
                        label={`Toggle multilingual for ${customer.email}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Small self-contained toggle switch — no external UI dep required.
 */
function ToggleSwitch({ checked, disabled, onChange, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={[
        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer',
        'rounded-full border-2 border-transparent transition-colors',
        'duration-200 ease-in-out focus:outline-none focus:ring-2',
        'focus:ring-brand-500 focus:ring-offset-2',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
        checked ? 'bg-brand-600' : 'bg-slate-300',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'inline-block h-5 w-5 transform rounded-full bg-white shadow',
          'ring-0 transition duration-200 ease-in-out',
          checked ? 'translate-x-5' : 'translate-x-0',
        ].join(' ')}
      />
    </button>
  )
}
